import { Divider, Tab, TabList, Tabs } from "@relex-solutions/fragments"
import { useMemo } from "react"
import { useTranslation } from "react-i18next"
import { useLocation, useNavigate } from "react-router"
import { MenuDisplayContext } from "@/domains/customers/constants/customerNavigation"
import { useGetFormMenuItems } from "@/domains/customers/hooks/useGetFormMenuItems"
import { useMicroFrontendUrls } from "@/domains/customers/hooks/useMicroFrontendUrls"
import { RESOURCE_ABILITIES } from "@/shared/constants/permissions"
import { CUSTOMERS_BASE_PATH } from "@/shared/constants/routePaths"
import { useAuthorization } from "@/shared/hooks/useAuthorization"
import { useFeatureFlags } from "@/shared/hooks/useFeatureFlags"
import type { MicroFrontendUrl } from "@/shared/types/MicroFrontendUrl"
import styles from "./CustomerNavigation.module.css"
import type { TabItem } from "./CustomerNavigation.types"
import { getCurrentTab, getCustomerId } from "./CustomerNavigation.utils"

export function CustomerNavigation() {
  const { t } = useTranslation()
  const { pathname } = useLocation()
  const navigate = useNavigate()
  const customerId = getCustomerId(pathname)
  const currentTab = getCurrentTab(pathname)
  const featureFlags = useFeatureFlags()
  const { enablePPProvisioning } = featureFlags
  const { can } = useAuthorization()

  const canViewDeployments = useMemo(
    () => can(RESOURCE_ABILITIES.VIEW_DEPLOYMENTS, `customer:${customerId}`),
    [can, customerId]
  )

  // Only fetch form menu items when feature is enabled AND user has view_deployments permission
  const { data: formMenuTitles } = useGetFormMenuItems(enablePPProvisioning && canViewDeployments, customerId)
  const { data: microFrontendUrls } = useMicroFrontendUrls()

  const formMenuTitleTabs: TabItem[] =
    formMenuTitles
      ?.filter(({ definitionType, isVisible }) => definitionType === MenuDisplayContext.CUSTOMER && isVisible !== false)
      .map(({ upsertId, menuTitle }) => ({
        value: `form/${upsertId}`,
        label: menuTitle
      })) ?? []

  const microFrontendTabs: TabItem[] =
    microFrontendUrls?.map(({ path, label }: MicroFrontendUrl) => ({
      value: path,
      label
    })) ?? []

  // Static tabs that are always present (including microfrontends)
  const staticTabs: TabItem[] = [
    {
      value: "overview",
      label: t("customer.tabs.overview")
    },
    {
      value: "plan-resources",
      label: t("customer.tabs.planresources")
    },
    ...microFrontendTabs
  ]

  // Dynamic tabs (form menus from menu_titles endpoint)
  const dynamicTabs: TabItem[] = formMenuTitleTabs

  const handleNavigationChange = (newTab: string | false) => {
    if (newTab) {
      void navigate(`${CUSTOMERS_BASE_PATH}/${customerId}/${newTab}`)
    }
  }

  const renderTab = (tab: TabItem) => (
    <Tab key={tab.value} value={tab.value} data-testid={tab.value}>
      {tab.label}
    </Tab>
  )

  return (
    <nav>
      <Tabs value={currentTab} onChange={handleNavigationChange} orientation="vertical">
        <TabList className={styles.tabList}>
          {[
            ...staticTabs.map(renderTab),
            ...(dynamicTabs.length > 0 ? [<Divider key="divider" />, ...dynamicTabs.map(renderTab)] : [])
          ]}
        </TabList>
      </Tabs>
    </nav>
  )
}
